// Partner Opportunities tab — SGNL-26.
//
// Sortable table grouped by tag_key. Each row aggregates opps that carry
// the tag with verdict='yes' AND confidence >= the global threshold:
//   • opp count
//   • total $$$ (sum of amount)
//   • open $$$ (not closed)
//   • won $$$
//
// Clicking a row drills into Deal Explorer with that tag pre-selected on
// the global filter (state.filters.tagKeys = [tag_key]) so the count on
// this tab matches the Deal Explorer row count exactly.
//
// Pure-vanilla DOM, no framework, every visual element reuses existing
// classes from styles.css (.tab-content, .data-table.sortable, .table-wrap,
// .row-count, .empty-bucket, .chip palette).

import { TAG_CONFIDENCE_THRESHOLD, computePartnerBreakdown, type PartnerBreakdownRow } from "../lib/aggregations";
import { esc, fmtMoney } from "../lib/format";
import { bindSortHeaders, makeSortable, type SortColumn, type SortState } from "../lib/sortable";
import { openTagRationalesPanel } from "../lib/tag_rationales_panel";
import { setActiveTab, setFilters } from "../state";
import type { OpportunityRow } from "../types";

const COLUMNS: SortColumn<PartnerBreakdownRow>[] = [
  { key: "tag_key", label: "Tag" },
  { key: "opp_count", label: "Opps", numeric: true },
  { key: "total_amount", label: "Total $", numeric: true },
  { key: "open_amount", label: "Open $", numeric: true },
  { key: "won_amount", label: "Won $", numeric: true },
];

let sortState: SortState<PartnerBreakdownRow> = {
  field: "total_amount",
  dir: "desc",
};

export function renderPartnersTab(host: HTMLElement, rows: OpportunityRow[]): void {
  // SGNL-109 — pinned threshold constant instead of f.tagThreshold so
  // partner counts stay coherent with applyFilters and the Deal Explorer.
  const partners = computePartnerBreakdown(rows, TAG_CONFIDENCE_THRESHOLD);
  const totalCount = partners.reduce((s, p) => s + p.opp_count, 0);
  const totalAmount = partners.reduce((s, p) => s + p.total_amount, 0);
  const { sorted, headHtml } = makeSortable(partners, COLUMNS, sortState);

  if (partners.length === 0) {
    host.innerHTML = `
      <div class="tab-content" data-testid="partners-tab">
        <div class="tab-header"><h2>Partner Opportunities</h2></div>
        <p class="empty-bucket">
          No tagged opps at confidence ≥ ${(TAG_CONFIDENCE_THRESHOLD * 100).toFixed(0)}%.
          Run another classifier pass to grow tag coverage.
        </p>
      </div>`;
    return;
  }

  host.innerHTML = `
    <div class="tab-content" data-testid="partners-tab">
      <div class="tab-header">
        <h2>Partner Opportunities <span class="row-count">${partners.length} tags · ${totalCount.toLocaleString()} opps · ${fmtMoney(totalAmount)}</span></h2>
        <span class="muted">Confidence ≥ ${(TAG_CONFIDENCE_THRESHOLD * 100).toFixed(0)}% · click a row to drill into Deal Explorer</span>
      </div>
      <div class="table-wrap">
        <table class="data-table sortable">
          ${headHtml}
          <tbody>
            ${sorted
              .map(
                (p) => `
              <tr data-tag-key="${esc(p.tag_key)}" data-testid="partners-row" style="cursor:pointer;">
                <td>
                  <span class="chip aqua" data-testid="partners-tag-chip">${esc(p.tag_key)}</span>
                  <button type="button" class="info-btn"
                          data-action="why-tagged"
                          data-tag-key="${esc(p.tag_key)}"
                          data-testid="partners-info-btn"
                          aria-label="Why are these opps tagged ${esc(p.tag_key)}?"
                          title="Show top-3 rationales">
                    ⓘ
                  </button>
                </td>
                <td class="num">${p.opp_count.toLocaleString()}</td>
                <td class="num">${fmtMoney(p.total_amount)}</td>
                <td class="num">${fmtMoney(p.open_amount)}</td>
                <td class="num">${fmtMoney(p.won_amount)}</td>
              </tr>`,
              )
              .join("")}
          </tbody>
        </table>
      </div>
    </div>`;

  bindSortHeaders(host, COLUMNS, sortState, (next) => {
    sortState = next;
    renderPartnersTab(host, rows);
  });

  // Drill: click a row → set tagKeys filter to just this tag, switch
  // to deals. Skipped when the click came from the ⓘ button (SGNL-104)
  // so the row click and the panel-open are distinct affordances.
  for (const tr of host.querySelectorAll<HTMLTableRowElement>('[data-testid="partners-row"]')) {
    tr.addEventListener("click", (ev) => {
      const target = ev.target as HTMLElement;
      if (target.closest('[data-action="why-tagged"]')) return;
      const key = tr.dataset.tagKey;
      if (!key) return;
      setFilters({ tagKeys: [key] });
      setActiveTab("deals");
    });
  }

  // SGNL-104 — ⓘ button delegated handler: open side panel with
  // top-3 rationales for the clicked tag. `stopPropagation` is
  // belt-and-braces (the row handler above already filters out
  // ⓘ-button clicks via closest) but keeps the button independent
  // of any future row-click rewiring.
  for (const btn of host.querySelectorAll<HTMLButtonElement>('[data-action="why-tagged"]')) {
    btn.addEventListener("click", (ev) => {
      ev.stopPropagation();
      const key = btn.dataset.tagKey;
      if (!key) return;
      void openTagRationalesPanel(key, rows);
    });
  }
}
